import {
  Avatar as AvatarImage,
  Button,
  Checkbox,
  Input,
  List,
  Select,
} from "antd";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../../../../app/store";
import SearchIcon from "../../../../../../assets/icons/SearchIcon";
import Avatar from "../../../../../../assets/images/avatar.svg";
import CustomModal from "../../../../../../components/ui/CustomModal";
import Loader from "../../../../../../components/ui/Loader";
import { useGetAllDepartmentQuery } from "../../../../../../features/superAdmin/employeeService/employeeManagementService";
import { useAddTicketCCMutation } from "../../../../../../features/superAdmin/ticketService/ticket/ticket.api";
import { useGetEmployeeListToAddCCQuery } from "../../../../../../features/superAdmin/ticketService/ticketServiceApi";
import useDebounce from "../../../../../../hooks/useDebounce";
import useError from "../../../../../../hooks/useError";
import { toast } from "../../../../../../lib/toast";
import { ModalComponentProps } from "../../../../../../types";
import { cn } from "../../../../../../utils/string";

export default function AddNewCCModal({
  handleClose,
  id: ticketId,
  ...rest
}: ModalComponentProps & { id?: number }) {
  const [selected, setSelected] = useState<number[]>([]);
  const [dept, setDept] = useState<number[]>([]);
  const { keyword, handleInputChange } = useDebounce();
  const auth = useAppSelector((state) => state.auth);
  const {
    isLoading,
    isError,
    error,
    data: allEmployeesData,
  } = useGetEmployeeListToAddCCQuery({
    params: {
      id: ticketId as number,
      search: keyword,
      dept_ids: dept ?? [],
    },
  });
  const [
    addCC,
    {
      isLoading: isAddLoading,
      isError: isAddError,
      error: addError,
      isSuccess: isAddSuccess,
    },
  ] = useAddTicketCCMutation();
  useError(isError, error, "Get all user list fail");
  useError(isAddError, addError, "Add CC fail");

  const handleAddCC = () => {
    if (!selected.length) {
      toast("success", "Please check at least one CC");
      return;
    }

    const data = {
      ticket_log_id: Number(ticketId as number),
      employees: selected?.map((item) => ({
        employee_id: item,
      })),
    };

    addCC({
      id: auth?.user?.id,
      data: data,
    });
  };
  const { data: deptData, isLoading: deptLoading } = useGetAllDepartmentQuery({
    params: {
      page: 1,
      limit: 100000000,
      status: "active",
    },
  });
  const employeeData = allEmployeesData?.data;

  const handleChecked = (id: number, checked: boolean) => {
    if (checked) {
      setSelected((prev) => [...prev, id]);
    } else {
      setSelected((prev) => prev.filter((item) => item !== id));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    const employeeIds = employeeData?.map((item) => item?.id);

    if (checked && employeeIds?.length) {
      setSelected(employeeIds);
    } else {
      setSelected([]);
    }
  };

  useEffect(() => {
    if (isAddSuccess) {
      toast("success", "Add CC successfully");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddSuccess]);

  return (
    <CustomModal
      {...rest}
      handleClose={handleClose}
      width={400}
      footer={null}
      title="Add New CC"
    >
      <div>
        <Input
          size="large"
          prefix={<SearchIcon />}
          placeholder="Search"
          onChange={handleInputChange}
        />
        <div className="mt-3 flex gap-3">
          <Select
            size="large"
            showSearch
            allowClear
            mode="multiple"
            placeholder="Department"
            optionFilterProp="label"
            disabled={deptLoading}
            options={deptData?.data.map((dep: any) => ({
              label: dep.name,
              value: dep.id,
            }))}
            value={dept}
            onChange={(e) => {
              setDept(e);
              setSelected([]);
            }}
            className="flex-1"
          />
        </div>
        <div
          className={cn(
            "flex justify-between items-center mt-2 py-2 pr-2 shadow-sm",
            (employeeData?.length || 0) > 5 ? "mr-2" : "",
          )}
        >
          <span>Select all</span>
          <span>
            <Checkbox
              disabled={employeeData?.length === 0 || isLoading}
              checked={employeeData?.length === selected.length}
              onChange={(e) => handleSelectAll(e.target.checked)}
            />
          </span>
        </div>
        <div className="h-[300px] overflow-y-auto pr-2 pt-2">
          {isLoading ? (
            <Loader />
          ) : (
            <List
              dataSource={employeeData}
              renderItem={(item) => {
                const isChecked = selected.includes(item.id);

                return (
                  <List.Item
                    onClick={() => handleChecked(item.id, !isChecked)}
                    className="!border-none !py-1.5 cursor-pointer"
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex gap-4 items-center">
                        <AvatarImage
                          src={(item.personalInfo?.pro_pic || Avatar) as string}
                          size={40}
                        />
                        <span className="text-[17px]">
                          {item.officialInfo?.name}
                        </span>
                      </div>
                      <Checkbox
                        checked={isChecked}
                        onChange={(e) =>
                          handleChecked(item.id, e.target.checked)
                        }
                      />
                    </div>
                  </List.Item>
                );
              }}
            />
          )}
        </div>
        <div className="py-2 flex justify-between ">
          <Button className="button-bordered-light h-10" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            className="button-primary-light h-10"
            loading={isAddLoading}
            onClick={handleAddCC}
          >
            Add
          </Button>
        </div>
      </div>
    </CustomModal>
  );
}
